<template>
  <Modal class="max-w-[600px]!">
    <Modal.Title>Select registered work</Modal.Title>

    <Modal.Body>
      <Breadcrumb
        :key="breadcrumbKey"
        :items="breadcrumbItems"
        collapse
        class="mb-[14px]"
        @select="handleBreadcrumbSelect"
      />

      <Table
        selection="single"
        :sticky-header="false"
        v-model:selected="selectedIds"
        v-model:sort="sort"
        :row-ids="fileRowIds"
      >
        <Table.Head>
          <Table.Header primary sortable field="name">Name</Table.Header>
          <Table.Header class="w-[250px] shrink-0" sortable field="timestamp">
            Dacr Timestamp
          </Table.Header>
        </Table.Head>

        <div
          class="max-h-[330px] overflow-y-auto overscroll-contain"
          @scroll="handleScroll"
        >
          <div
            v-if="loading && !items.length"
            class="flex h-[160px] items-center justify-center"
          >
            <Spinner :size="22" />
          </div>

          <template v-else>
            <Table.Row
              v-for="item in items"
              :id="item.id"
              :key="item.id"
              @row-click="handleRowClick(item)"
            >
              <template #media>
                <div class="w-6">
                  <Thumbnail
                    v-if="isFile(item)"
                    :type="fileTypeToThumbnail(item.type)"
                    :src="thumbSrc(item)"
                  />
                  <Thumbnail v-else type="folder" />
                </div>
              </template>

              <Table.Cell primary>
                <span class="truncate">{{ item.name }}</span>
              </Table.Cell>

              <Table.Cell class="w-[250px] shrink-0">
                <template v-if="isFile(item)">
                  <span
                    class="mr-[10px] flex size-6 shrink-0 items-center justify-center"
                  >
                    <DacrMarkIcon class="size-[18px] text-gray-800" />
                  </span>
                  <span class="truncate">{{ timestampLabel(item) }}</span>
                  <span
                    v-if="moreCount(item)"
                    class="ml-2 flex shrink-0 items-center gap-x-1 text-[12px] font-medium leading-[18px] text-gray-500"
                  >
                    +{{ moreCount(item) }} more
                    <ChevronUpIcon class="size-[10px] rotate-180" />
                  </span>
                </template>
              </Table.Cell>
            </Table.Row>

            <div
              v-if="!items.length"
              class="px-6 py-10 text-center text-[14px] text-gray-500"
            >
              No registered works here
            </div>
          </template>
        </div>
      </Table>
    </Modal.Body>

    <Modal.Footer>
      <Button :size="isMobile ? 'lg' : 'md'" variant="stroke" @click="close()">
        Cancel
      </Button>
      <Button
        :size="isMobile ? 'lg' : 'md'"
        variant="primary"
        :disabled="!selected"
        @click="confirm()"
      >
        Select
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  Modal,
  Table,
  Thumbnail,
  Button,
  Breadcrumb,
  useModal,
} from '@dacr/library'
import { ChevronUpIcon, DacrMarkIcon } from '@dacr/icons'

import Spinner from '@/shared/components/Spinner.vue'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { useDevice } from '@/shared/composables/useDevice'
import type { File, Item } from '@/shared/graphql/router/graphql'
import { useSelectCopyright } from '../useSelectCopyright'

const { close } = useModal()
const { isMobile } = useDevice()
const {
  loading,
  loadMore,
  sort,
  items,
  breadcrumbPath,
  breadcrumbItems,
  thumbSrc,
  timestampLabel,
  moreCount,
  handleBreadcrumbSelect,
  openFolder,
  selected,
  confirm,
  isFile,
  isFolder,
} = useSelectCopyright()

const handleScroll = (e: Event) => {
  const el = e.target as HTMLElement
  if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) loadMore()
}

const breadcrumbKey = computed(() => breadcrumbPath.value.join('-'))

const fileRowIds = computed(() =>
  items.value.filter((item) => isFile(item)).map((item) => item.id)
)

const selectedIds = computed<string[]>({
  get: () => (selected.value ? [selected.value.id] : []),
  set: (ids) => {
    const id = ids[0]
    selected.value = id
      ? items.value.find((item): item is File => isFile(item) && item.id === id)
      : undefined
  },
})

const handleRowClick = (item: Item) => {
  if (isFolder(item)) return openFolder(item)
}
</script>
